import { useState, useMemo, useEffect } from "react";
import { ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectOptionGroup {
  groupLabel: string;
  options: MultiSelectOption[];
}

interface MultiSelectFilterProps {
  label: string;
  options?: MultiSelectOption[];
  optionGroups?: MultiSelectOptionGroup[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  className?: string;
  maxHeight?: string;
  "data-testid"?: string;
}

export function MultiSelectFilter({
  label,
  options,
  optionGroups,
  selected,
  onChange,
  disabled = false,
  className,
  maxHeight = "14rem",
  "data-testid": testId,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Derive a flat list of all options regardless of whether groups are used
  const flatOptions = useMemo<MultiSelectOption[]>(() => {
    if (optionGroups) {
      return optionGroups.flatMap((g) => g.options);
    }
    return options ?? [];
  }, [options, optionGroups]);

  // "Unfiltered" when nothing is selected OR all options are explicitly selected
  const isUnfiltered = selected.length === 0 || selected.length === flatOptions.length;

  // When using groups: build filtered groups (hiding empty groups during search)
  // When using flat options: build a flat filtered list
  const { filteredFlat, filteredGroups } = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (optionGroups) {
      const fg = optionGroups
        .map((g) => ({
          groupLabel: g.groupLabel,
          options: q
            ? g.options.filter(
                (o) =>
                  o.label.toLowerCase().includes(q) ||
                  o.value.toLowerCase().includes(q)
              )
            : g.options,
        }))
        .filter((g) => g.options.length > 0);
      return { filteredFlat: fg.flatMap((g) => g.options), filteredGroups: fg };
    }
    const src = options ?? [];
    const flat = q
      ? src.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            o.value.toLowerCase().includes(q)
        )
      : src;
    return { filteredFlat: flat, filteredGroups: null };
  }, [options, optionGroups, search]);

  const triggerLabel = useMemo(() => {
    if (isUnfiltered) return `All ${label}`;
    if (selected.length === 1) {
      const opt = flatOptions.find((o) => o.value === selected[0]);
      return opt?.label ?? selected[0];
    }
    return `${selected.length} ${label}`;
  }, [isUnfiltered, selected, label, flatOptions]);

  const handleToggle = (value: string) => {
    if (selectedSet.has(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      const next = [...selected, value];
      // If all are now explicitly selected, normalize back to "all" (empty)
      onChange(next.length === flatOptions.length ? [] : next);
    }
  };

  // "All" checks every option explicitly
  const handleSelectAll = () => {
    if (search.trim()) {
      const toAdd = filteredFlat.map((o) => o.value).filter((v) => !selectedSet.has(v));
      const next = [...selected, ...toAdd];
      onChange(next.length === flatOptions.length ? [] : next);
    } else {
      onChange(flatOptions.map((o) => o.value));
    }
  };

  // "Clear" unchecks everything
  const handleClearAll = () => {
    if (search.trim()) {
      const toRemove = new Set(filteredFlat.map((o) => o.value));
      onChange(selected.filter((v) => !toRemove.has(v)));
    } else {
      onChange([]);
    }
  };

  const renderOption = (option: MultiSelectOption) => (
    <label
      key={option.value}
      className="flex items-center gap-2 px-1 py-1 rounded-sm hover-elevate cursor-pointer text-sm"
      data-testid={`label-multi-select-${label.toLowerCase()}-${option.value}`}
    >
      <Checkbox
        checked={selectedSet.has(option.value)}
        onCheckedChange={() => handleToggle(option.value)}
        data-testid={`checkbox-multi-select-${label.toLowerCase()}-${option.value}`}
      />
      <span className="truncate">{option.label}</span>
    </label>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between font-normal gap-1",
            !isUnfiltered && "border-primary/50 text-primary",
            className
          )}
          disabled={disabled}
          data-testid={testId}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <div className="p-2 space-y-2">
          <Input
            placeholder={`Search…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
          <div className="flex items-center justify-between gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="h-6 text-xs px-2"
            >
              {search.trim() ? "Add visible" : "All"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-6 text-xs px-2"
            >
              Clear
            </Button>
          </div>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight }}>
          <div className="px-2 pb-2 space-y-0.5">
            {filteredFlat.length === 0 ? (
              <div className="py-3 text-center text-xs text-muted-foreground">
                No matching options
              </div>
            ) : filteredGroups ? (
              filteredGroups.map((group) => (
                <div key={group.groupLabel}>
                  <div className="px-1 pt-2 pb-0.5 text-xs font-medium text-muted-foreground select-none">
                    {group.groupLabel}
                  </div>
                  {group.options.map(renderOption)}
                </div>
              ))
            ) : (
              filteredFlat.map(renderOption)
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function parseMultiParam(param: string | null): string[] {
  if (!param || param === "all") return [];
  return param.split(",").filter(Boolean);
}

export function serializeMultiParam(values: string[]): string | null {
  if (values.length === 0) return null;
  return values.join(",");
}
